import * as Sentry from "@sentry/react";
import axios from "axios";

import { emitUnauthorized, shouldEmitUnauthorized } from "./authEvents";
import { Pagination } from "./requestWithToken";

export type BaseResponse<T> = {
  data: T;
  pagination: T extends unknown[] ? Pagination : undefined;
};

type Response<T, K = unknown> = K extends object ? BaseResponse<T> & K : BaseResponse<T>;

/**
 * For a write whose button is held until the promise settles. A connection
 * dropped mid-flight never settles it, so the button would spin for good.
 */
export const WRITE_TIMEOUT_MS = 30_000;

/** Marks a cancellation that was our deadline rather than a caller giving up. */
const STALLED = "rakamin:request-stalled";

function request<Data, Meta>(
  url: string,
  httpMethod: "GET" | "POST" | "PATCH" | "DELETE" | "PUT",
  token?: string,
  data?: object,
  params?: object,
  customHeaders?: object,
  skipUnauthorizedHandling?: boolean,
  /** Off by default: ~170 call sites here, some legitimately slow (exports, AI). */
  timeout?: number
): Promise<Response<Data, Meta>> {
  /* axios is given the timeout and does not act on it when a connection is cut
     mid-flight: the request neither answers nor fails, and the promise never
     settles. Measured on a publish held at 5s — nothing after 30. So the
     deadline is kept here, and it cancels the request rather than only giving
     up on it, which axios does honour. */
  const deadline = timeout ? axios.CancelToken.source() : null;
  const timer = deadline ? setTimeout(() => deadline.cancel(STALLED), timeout) : undefined;
  const stopTimer = () => clearTimeout(timer);

  return axios
    .request({
      method: httpMethod,
      url,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...customHeaders,
      },
      data,
      params,
      ...(timeout ? { timeout } : {}),
      ...(deadline ? { cancelToken: deadline.token } : {}),
    })
    .then((res) => {
      stopTimer();

      return res.data;
    })
    .catch((err) => {
      stopTimer();

      let errorMessage = err;

      /* Our own deadline, or axios's if it ever fires. A timeout carries no
         `response`, so it would otherwise surface as the raw axios message. */
      if (err?.message === STALLED || (err?.code === "ECONNABORTED" && !err?.response)) {
        errorMessage = {
          /* A timeout says we stopped waiting, not that the server did nothing:
             the request may well have arrived and been carried out. Telling
             someone their work was not saved invites them to repeat it.

             And not "reload": the Website Content draft and an unsaved article
             body live only in memory, so a reload is the one action that throws
             that work away. Another tab answers the same question for free. */
          message:
            "The connection stalled and the request was stopped. It may or may not have reached the server. Check the live content in another tab before trying again.",
        };
      }

      if (err?.response) {
        const statusCode = err?.response?.status;
        const statusPrefix = Math.floor(statusCode / 100); // Get the first digit (e.g., 4 for 4xx)

        // A 401 on a token-bearing request means the server rejected our token
        // (revoked, expired server-side, permissions changed). Clean it up and
        // prompt re-login. See `shouldEmitUnauthorized` for the exact gate.
        if (
          shouldEmitUnauthorized({ statusCode, hasToken: Boolean(token), skipUnauthorizedHandling })
        ) {
          emitUnauthorized();
        }

        switch (statusPrefix) {
          case 5:
            errorMessage = {
              message:
                "We're unable to process your request at this time due to temporary technical issues. Please try again later or contact Customer Support if the problem persists.",
              // `status` is added so 5xx is distinguishable from any other
              // failure. Deliberately NOT `response`: getErrorMessage() reads
              // `response.data.errors[0].message` in preference to `message`,
              // so carrying the raw body here would replace this friendly copy
              // with the backend's error text at ~300 call sites app-wide.
              status: statusCode,
            };
            break;
        }
      }

      Sentry.captureException(errorMessage);
      throw errorMessage;
    });
}

export default request;
